import {useLazyLoadQuery, usePaginationFragment} from "react-relay";
import {ANSWERS_STATISTIC_FRAGMENT, QUERY} from "./answers-statistics-table.graphql";
import {answersStatisticsTable_Refetch} from "../../../../__generated__/answersStatisticsTable_Refetch.graphql";
import {
    answersStatisticsTable_Fragment$key
} from "../../../../__generated__/answersStatisticsTable_Fragment.graphql";
import {answersStatisticsTable_Query} from "../../../../__generated__/answersStatisticsTable_Query.graphql";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {useContext, useEffect, useMemo} from "react";
import {AnswersStatisticsFilterState, AnswersStatisticsSearchContext} from "../filters/AnswersStatisticsSearchContext";
import {debounce} from "lodash";
import {htmlTagsInTextRemover} from "../../../../infecto-lms-admin/functions/htmlTagsInTextRemover";
import {SpinnerIcon} from "./answer-statistics-table.styles";

export const AnswersStatisticsTableComponent = () => {
    const {filters} = useContext(AnswersStatisticsSearchContext)

    const query = useLazyLoadQuery<answersStatisticsTable_Query>(QUERY, {
        filterByTitle: filters.filterByTitle,
        greaterThanDate: filters.greaterThanDate,
        lessThanDate: filters.lessThanDate,
        filterByTagIds: filters.filterByTagIds},
        {fetchPolicy: "store-and-network"})

    const {
        data: {
            Admin: {
                Statistic : {
                    AnswersStatistic: { edges: answersStatistic },
                },
            },
        },
        refetch,
        hasNext,
        hasPrevious,
        isLoadingNext,
        loadPrevious, loadNext,
    } = usePaginationFragment<answersStatisticsTable_Refetch, answersStatisticsTable_Fragment$key>(
        ANSWERS_STATISTIC_FRAGMENT,
        query,
    );

    const headerCustomStyle = {background: "lightgrey"}

    const answerColumns = () =>
        Array.from({ length: 10 }, (_, idx) => (
            <Column
                headerStyle={headerCustomStyle}
                header={`Antwort ${idx + 1}`}
                body={(item) =>
                    item.answerStatistics
                        ?.filter((a: { index: number; }) => a.index === idx)
                        .map((ans: { checkedTimes: any; }) => `${ans.checkedTimes}`)
                }
            />
        ))

    const debouncedRefetch = (filters: AnswersStatisticsFilterState) => {
        refetch(
            {
                filterByTitle: filters.filterByTitle,
                greaterThanDate: filters.greaterThanDate,
                lessThanDate: filters.lessThanDate,
                filterByTagIds: filters.filterByTagIds,
                first: 1,
            },
            { fetchPolicy: "network-only" },
        );
    };


    const debouncedEventHandler = useMemo(() => debounce(debouncedRefetch, 1000), []);
    useEffect(() => {
        debouncedEventHandler(filters);
    }, [filters]);


    return (
        <div>
            <DataTable
                showGridlines
                emptyMessage="Keine Ergebnisse"
                className="mb-3"
                value={
                    answersStatistic?.flatMap((e) =>
                        e?.node?.elementStatistics?.map((elStat) => ({
                            ...elStat,
                            rootNodeTitle: e.node.rootNodeTitle,
                        })) || []
                    ) || []
                }
            >
                <Column
                    header=""
                    headerStyle={{ minWidth: '380px' }}
                    bodyStyle={{ minWidth: '380px' }}
                    body={(item) => `${htmlTagsInTextRemover(`${item.rootNodeTitle} - ${item.question}`)}`}
                />
                <Column
                    headerStyle={headerCustomStyle}
                    header="Versuche"
                    body={(item) => item.totalTries}
                />
                <Column
                    headerStyle={headerCustomStyle}
                    header="Richtige Antworte"
                    body={(item) =>
                        // eslint-disable-next-line
                        item.answerStatistics
                            ?.filter((ans: { isCorrect: any; }) => ans.isCorrect) //eslint-disable
                            .map((ans: { index: number; checkedTimes: any; }) => `${ans.checkedTimes}`)
                    }
                />
                <Column
                    headerStyle={headerCustomStyle}
                    header="% Richtige Antworte"
                    body={(item) => `${item.correctPercentage}%`
                    }
                />
                <Column
                    headerStyle={headerCustomStyle}
                    header="Richtige Antwort"
                    body={(item) => {
                        const correct = item.answerStatistics?.filter((a: { isCorrect: any; }) => a.isCorrect) || [];

                        if (!correct.length) return "—";

                        return correct
                            .map((a: { index: number; }) => {
                                return `${a.index + 1}`;
                            })
                    }}
                />
                <Column
                    headerStyle={headerCustomStyle}
                    header="Meiste falsche Antwort"
                    body={(item) => {
                        const wrongAnswers = item.answerStatistics?.filter((a: { isCorrect: any; }) => !a.isCorrect) || [];
                        if (!wrongAnswers.length) return "—";
                        const mostTried = wrongAnswers.reduce((max: { checkedTimes: any; }, a: { checkedTimes: any; }) =>
                            (a.checkedTimes || 0) > (max.checkedTimes || 0) ? a : max
                        );
                        return `${mostTried.index + 1 }`;
                    }}
                />
                {answerColumns()}

            </DataTable>
            <div className="flex justify-content-center align-items-center">
                <Button
                    disabled={!hasPrevious}
                    onClick={() => loadPrevious(1)}
                    className="mr-3 p-button-secondary"
                >
                    Zurück
                </Button>
                <Button
                    icon ={isLoadingNext && <SpinnerIcon className="pi pi-spin pi-spinner"></SpinnerIcon>}
                    className="p-button-secondary"
                    disabled={!hasNext}
                    onClick={() => loadNext(1)}
                >
                    Weiter
                </Button>
            </div>
        </div>
    );
};

